import { cn } from "../lib/utils";
import { JSX } from "react";

type commonVariant = "h1" | "h2" | "h3" | "h4" | "h5";
type spanVariant =
  | "body1"
  | "body2"
  | "body3"
  | "body4"
  | "subtitle1"
  | "miniText";

type Variant = commonVariant | spanVariant;

const classNames: { [key in Variant]: string } = {
  h1: "font-[Work Sans, sans-serif] font-semibold text-3xl",
  h2: "font-[Work Sans, sans-serif] font-medium text-3xl",
  h3: "font-[Work Sans, sans-serif] font-medium text-2xl",
  h4: "font-[Work Sans, sans-serif] font-semibold text-2xl",
  h5: "font-[Work Sans, sans-serif] font-semibold text-xl",
  subtitle1: "font-[Work Sans, sans-serif] font-semibold text-lg",
  body1: "font-[Work Sans, sans-serif] font-medium text-base",
  body2: "font-[Work Sans, sans-serif] text-base",
  body3: "font-[Work Sans, sans-serif] font-light text-sm",
  body4: "font-[Work Sans, sans-serif] text-sm",
  miniText: "font-[Work Sans, sans-serif] font-medium text-xs",
};

type CommonProps = JSX.IntrinsicElements["h1" | "h2" | "h3" | "h4" | "h5"] & {
  variant: commonVariant;
};

type SubtleProps = JSX.IntrinsicElements["span"] & {
  variant: spanVariant;
};

type Props = CommonProps | SubtleProps;

const Typography = ({ className, variant, ...props }: Props) => {
  const inputs = cn(classNames[variant], className);

  switch (variant) {
    case "h1":
      return (
        <h1
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["h1"], "className">)}
        >
          {(props as JSX.IntrinsicElements["h1"]).children}
        </h1>
      );
    case "h2":
      return (
        <h2
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["h2"], "className">)}
        >
          {(props as JSX.IntrinsicElements["h2"]).children}
        </h2>
      );
    case "h3":
      return (
        <h3
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["h3"], "className">)}
        >
          {(props as JSX.IntrinsicElements["h3"]).children}
        </h3>
      );
    case "h4":
      return (
        <h4
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["h4"], "className">)}
        >
          {(props as JSX.IntrinsicElements["h4"]).children}
        </h4>
      );
    case "h5":
      return (
        <h5
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["h5"], "className">)}
        >
          {(props as JSX.IntrinsicElements["h5"]).children}
        </h5>
      );
    case "subtitle1":
      return (
        <p
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["p"], "className">)}
        >
          {(props as JSX.IntrinsicElements["p"]).children}
        </p>
      );
    case "body1":
      return (
        <p
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["p"], "className">)}
        >
          {(props as JSX.IntrinsicElements["p"]).children}
        </p>
      );
    case "body2":
      return (
        <p
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["p"], "className">)}
        >
          {(props as JSX.IntrinsicElements["p"]).children}
        </p>
      );
    case "body3":
      return (
        <p
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["p"], "className">)}
        >
          {(props as JSX.IntrinsicElements["p"]).children}
        </p>
      );
    case "body4":
      return (
        <p
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["p"], "className">)}
        >
          {(props as JSX.IntrinsicElements["p"]).children}
        </p>
      );
    case "miniText":
      return (
        <p
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["p"], "className">)}
        >
          {(props as JSX.IntrinsicElements["p"]).children}
        </p>
      );
    default:
      return (
        <p
          className={inputs}
          {...(props as Omit<JSX.IntrinsicElements["p"], "className">)}
        />
      );
  }
};

export default Typography;
